import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import { DIAS_SEMANA, type Cliente } from '../../db/tipos'
import { definirDiaDeVisita } from '../../visitas/logica'
import {
  contarPorDia,
  filtrarParaMontagem,
  opcoesDeLocal,
  ordenarParaMontagem,
  percentualComDia,
  type OrdemMontagem,
  type TipoLocal,
} from '../../visitas/montagem'
import { useSalvamento } from '../../componentes/useSalvamento'

// Tela redesenhada pelo print dela (08/10). Sem km, "Proximos", mapa, seletor
// de semana e leitor de codigo: o cliente nao tem localizacao no banco e o dia
// de visita e fixo, vale para toda semana (decisoes 1-4 dela).

// A faixa da esquerda do card = classificacao do cliente (decisao 5a).
const COR_CLASSIFICACAO: Record<NonNullable<Cliente['potencial']>, string> = {
  A: 'bg-amber-400',
  B: 'bg-slate-400',
  C: 'bg-orange-700',
  D: 'bg-zinc-300',
}

type Aba = 'todos' | TipoLocal

export default function MontarRota() {
  const [busca, setBusca] = useState('')
  const [soSemDia, setSoSemDia] = useState(false)
  const [diaTocado, setDiaTocado] = useState<number | null>(null)
  const [aba, setAba] = useState<Aba>('todos')
  const [local, setLocal] = useState('')
  const [ordem, setOrdem] = useState<OrdemMontagem>('nome')
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set())
  const { estado, executar } = useSalvamento()

  // useLiveQuery, nunca useEffect: sem isto a tela abre vazia no aparelho
  // recem-aberto, porque o dado do servidor chega depois -- e, pior aqui, o
  // contador nao mexeria ao mudar o dia de um cliente.
  const clientes = useLiveQuery(
    () => banco.clientes.filter((c) => c.apagado_em === null && c.status === 'ativo').toArray(),
    [],
  )

  const todos = clientes ?? []
  const visiveis = ordenarParaMontagem(
    filtrarParaMontagem(todos, busca, soSemDia, {
      dia: diaTocado,
      local: aba === 'todos' ? undefined : { tipo: aba, valor: local },
    }),
    ordem,
  )

  // Os buracos da semana de relance: "quarta esta zerada" e a informacao que
  // faz ele mexer na rota.
  const porDia = contarPorDia(todos)
  const percentual = percentualComDia(todos)
  const hoje = new Date().getDay()

  function trocarAba(nova: Aba) {
    setAba(nova)
    setLocal('')
  }

  function alternarSelecao(id: string) {
    setSelecionados((atual) => {
      const novo = new Set(atual)
      if (novo.has(id)) novo.delete(id)
      else novo.add(id)
      return novo
    })
  }

  // Decisao 6a: marcar varios e dar o dia de todos de uma vez.
  function definirDiaDosSelecionados(valor: string) {
    if (valor === 'escolher') return
    const dia = valor === '' ? null : Number(valor)
    const ids = [...selecionados]
    void executar(async () => {
      for (const id of ids) await definirDiaDeVisita(id, dia)
    })
    setSelecionados(new Set())
  }

  const abas: { valor: Aba; rotulo: string }[] = [
    { valor: 'todos', rotulo: 'Todos' },
    { valor: 'cidade', rotulo: 'Cidade' },
    { valor: 'bairro', rotulo: 'Bairro' },
  ]

  return (
    <section className="pb-28">
      <div className="bg-[#0B4A47] px-4 pt-4 pb-6 text-white">
        <h2 className="text-xl font-bold">Rota da semana</h2>
        <p className="text-sm text-teal-100">Organize suas visitas e aumente seus resultados</p>
      </div>

      <div className="-mt-3 rounded-t-2xl bg-slate-50 px-4 pt-4 space-y-3">
        {/* Decisao 7: tocar no dia filtra a lista; tocar de novo solta. */}
        <div data-testid="contadores-semana" className="grid grid-cols-7 gap-1.5">
          {DIAS_SEMANA.map((dia, indice) => {
            const ativo = diaTocado === indice
            return (
              <button
                key={dia}
                type="button"
                aria-pressed={ativo}
                aria-label={`${dia}: ${porDia[indice]} clientes`}
                onClick={() => setDiaTocado(ativo ? null : indice)}
                className={`relative rounded-xl py-2 text-center shadow-sm ${
                  ativo ? 'bg-[#0B4A47] text-white' : 'bg-white text-slate-800'
                }`}
              >
                {indice === hoje && (
                  <span
                    className={`absolute top-1 left-1/2 -translate-x-1/2 h-1.5 w-1.5 rounded-full ${
                      ativo ? 'bg-white' : 'bg-[#0B4A47]'
                    }`}
                  />
                )}
                <span className="block text-xs">{dia.slice(0, 3)}</span>
                <strong className="block text-lg leading-tight">{porDia[indice]}</strong>
              </button>
            )
          })}
        </div>

        <div className="flex items-center gap-3 text-sm text-slate-600">
          <span>
            Total: <strong className="text-slate-900">{todos.length}</strong> clientes
          </span>
          <div className="ml-auto h-2 w-24 rounded-full bg-slate-200" aria-hidden>
            <div className="h-2 rounded-full bg-[#0B4A47]" style={{ width: `${percentual}%` }} />
          </div>
          <span title="Clientes que já têm dia de visita">{percentual}% com dia</span>
        </div>

        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar cliente (nome, CNPJ ou código)"
          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5"
        />

        <div className="flex rounded-full bg-white p-1 shadow-sm">
          {abas.map((a) => (
            <button
              key={a.valor}
              type="button"
              aria-pressed={aba === a.valor}
              onClick={() => trocarAba(a.valor)}
              className={`flex-1 rounded-full py-2 text-sm ${
                aba === a.valor ? 'bg-[#0B4A47] font-semibold text-white' : 'text-slate-700'
              }`}
            >
              {a.rotulo}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          {aba !== 'todos' && (
            <label className="flex-1 min-w-0 text-xs text-slate-500">
              {aba === 'cidade' ? 'Cidade' : 'Bairro'}
              <select
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm text-slate-800"
              >
                <option value="">{aba === 'cidade' ? 'Todas as cidades' : 'Todos os bairros'}</option>
                {opcoesDeLocal(todos, aba).map((nome) => (
                  <option key={nome} value={nome}>{nome}</option>
                ))}
              </select>
            </label>
          )}
          <label className="flex-1 min-w-0 text-xs text-slate-500">
            Ordenar por
            <select
              value={ordem}
              onChange={(e) => setOrdem(e.target.value as OrdemMontagem)}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm text-slate-800"
            >
              <option value="nome">Nome</option>
              <option value="bairro">Bairro</option>
            </select>
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={soSemDia} onChange={(e) => setSoSemDia(e.target.checked)} />
          Mostrar só quem ainda não tem dia
        </label>

        {/* Os estados vem de rotuloSalvamento.ts: 'ocioso' | 'salvando' | 'salvo' | 'erro'. */}
        <div className="flex justify-end min-h-5" data-testid="selo-salvo">
          {estado === 'salvo' && <span className="text-xs text-emerald-700">✓ Salvo</span>}
          {estado === 'erro' && <span className="text-xs text-red-700">Não salvou — tente de novo</span>}
        </div>

        <ul className="space-y-2">
          {visiveis.map((cliente) => {
            const nome = cliente.nome_fantasia || cliente.razao_social
            const lugar = [cliente.bairro, [cliente.municipio, cliente.uf].filter(Boolean).join('/')]
              .filter(Boolean)
              .join(' - ')
            const marcado = selecionados.has(cliente.id)
            return (
              <li
                key={cliente.id}
                className={`relative overflow-hidden rounded-xl bg-white shadow-sm flex items-center gap-2 py-3 pl-4 pr-3 ${
                  marcado ? 'ring-2 ring-[#0B4A47]' : ''
                }`}
              >
                <span
                  className={`absolute inset-y-0 left-0 w-1.5 ${
                    cliente.potencial ? COR_CLASSIFICACAO[cliente.potencial] : 'bg-slate-200'
                  }`}
                  aria-hidden
                />
                <input
                  type="checkbox"
                  aria-label={`Selecionar ${nome}`}
                  checked={marcado}
                  onChange={() => alternarSelecao(cliente.id)}
                  className="h-5 w-5 shrink-0 accent-[#0B4A47]"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{nome}</p>
                  {cliente.cnpj_cpf && <p className="text-xs text-slate-500">{cliente.cnpj_cpf}</p>}
                  {lugar && <p className="text-xs text-slate-600 truncate">📍 {lugar}</p>}
                </div>
                <select
                  aria-label={`Dia de visita de ${nome}`}
                  value={cliente.dia_visita ?? ''}
                  onChange={(e) => {
                    const valor = e.target.value === '' ? null : Number(e.target.value)
                    void executar(() => definirDiaDeVisita(cliente.id, valor))
                  }}
                  className={`shrink-0 rounded-lg border px-2 py-1.5 text-sm ${
                    cliente.dia_visita !== null ? 'border-teal-200 bg-teal-50' : 'border-slate-300 bg-white'
                  }`}
                >
                  <option value="">—</option>
                  {DIAS_SEMANA.map((dia, indice) => (
                    <option key={dia} value={indice}>{dia}</option>
                  ))}
                </select>
              </li>
            )
          })}
        </ul>

        {visiveis.length === 0 && (
          <p className="text-sm text-slate-500">
            {soSemDia ? 'Todo mundo já tem dia de visita.' : 'Nenhum cliente encontrado.'}
          </p>
        )}
      </div>

      {/* Barra de selecao: fica acima do rodape de abas do Layout. */}
      <div className="fixed inset-x-0 bottom-16 z-20 px-4">
        <div className="mx-auto flex max-w-md items-center gap-3 rounded-2xl bg-white p-3 shadow-lg border border-slate-200">
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-900">{selecionados.size} selecionados</p>
            <p className="text-xs text-slate-500">Selecione os clientes e defina o dia</p>
          </div>
          <select
            aria-label="Dia de visita dos selecionados"
            value="escolher"
            disabled={selecionados.size === 0}
            onChange={(e) => definirDiaDosSelecionados(e.target.value)}
            className="shrink-0 rounded-xl bg-[#0B4A47] px-3 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
          >
            <option value="escolher" disabled>Definir dia ›</option>
            <option value="">Sem dia</option>
            {DIAS_SEMANA.map((dia, indice) => (
              <option key={dia} value={indice}>{dia}</option>
            ))}
          </select>
        </div>
      </div>
    </section>
  )
}
